import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Check, Loader2, RefreshCw, Server } from 'lucide-react';
import { apiService, DivergenceFileDTO, DivergenceReportDTO } from '@/services/api.service';
import { useGameStore } from '@/stores/game.store';
import styles from './ImageDivergences.module.scss';

const fmtDate = (ts: number) => new Date(ts).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });

/** « World, par rapport à Lobby : clé, clé » pour chaque version qui n'est pas la référence. */
function VariantLines({ file }: { file: DivergenceFileDTO }) {
  const ref = file.variants.find((v) => v.reference);
  return (
    <>
      {file.variants.filter((v) => !v.reference).map((v) => (
        <div key={v.containerIds.join(',')} className={styles.variant}>
          <strong>{v.containerNames.join(', ')}</strong>
          {ref && <>, par rapport à {ref.containerNames.join(', ')}</>} :{' '}
          {v.differs.map((k, i) => (
            <span key={k}>{i > 0 && ', '}<span className={styles.key}>{k}</span></span>
          ))}
        </div>
      ))}
    </>
  );
}

/**
 * Écarts de réglages entre les images du projet actif (World et Lobby de NationsGlory, les images de
 * Paladium) : un même fichier de config qui n'est pas le même partout. Lu sur ce que les images
 * publient, sections propres à chaque serveur (bases, RabbitMQ, API internes) ignorées. Un écart
 * voulu se marque comme tel ; il revient en alerte dès que l'un des fichiers change.
 */
export function ImageDivergences({ canAck }: { canAck: boolean }) {
  const project = useGameStore((s) => s.activeGameId);
  const [report, setReport] = useState<DivergenceReportDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [showAcked, setShowAcked] = useState(false);

  const load = useCallback(async (refresh: boolean) => {
    setLoading(true);
    const r = await apiService.getContainerDivergences(refresh);
    setLoading(false);
    if (r.data) { setReport(r.data); setError(null); } else { setError(r.error ?? 'relevé illisible'); }
  }, []);

  useEffect(() => { setReport(null); void load(false); }, [project, load]);

  const ack = async (f: DivergenceFileDTO, acknowledged: boolean) => {
    setBusy(f.path);
    const r = await apiService.ackContainerDivergence(f.path, f.fingerprint, acknowledged);
    setBusy(null);
    if (r.data) setReport(r.data); else setError(r.error ?? 'Échec');
  };

  const open = report?.files.filter((f) => !f.acknowledged) ?? [];
  const acked = report?.files.filter((f) => f.acknowledged) ?? [];
  const names = report?.images.map((i) => i.name).join(', ') ?? '';

  return (
    <section className={styles.box}>
      <div className={styles.head}>
        <Server size={14} aria-hidden />
        <span className={styles.headTitle}>Écarts entre les images</span>
        {report && (
          <span className={styles.headCount}>
            {report.identicalFiles} fichier{report.identicalFiles > 1 ? 's' : ''} identique{report.identicalFiles > 1 ? 's' : ''} sur{' '}
            {report.comparedFiles} commun{report.comparedFiles > 1 ? 's' : ''} · relevé du {fmtDate(report.generatedAt)}
          </span>
        )}
        <span className={styles.spacer} />
        <button className={styles.iconBtn} onClick={() => void load(true)} disabled={loading} title="Refaire le relevé">
          <RefreshCw size={14} className={loading ? styles.spin : undefined} />
        </button>
      </div>
      <div className={styles.body}>
        {!report && loading && <div className={styles.muted}><Loader2 size={12} className={styles.spin} /> Lecture des images…</div>}
        {error && <div className={styles.muted}>Écarts non lus : {error}</div>}
        {report && report.images.length < 2 && (
          <div className={styles.muted}>Moins de deux images copiées dans le panel pour ce projet : rien à comparer.</div>
        )}
        {report && report.images.length >= 2 && open.length === 0 && (
          <div className={styles.aligned}>
            <Check size={14} aria-hidden /> {names} portent les mêmes réglages
            {acked.length > 0 && <>, hors {acked.length} écart{acked.length > 1 ? 's' : ''} marqué{acked.length > 1 ? 's' : ''} comme voulu{acked.length > 1 ? 's' : ''}</>}.
          </div>
        )}
        {open.length > 0 && (
          <div className={styles.alert} role="status">
            <AlertTriangle size={14} aria-hidden />
            <div>
              <strong>{open.length} fichier{open.length > 1 ? 's' : ''} de réglages ne {open.length > 1 ? 'sont' : 'est'} pas
              {' '}le{open.length > 1 ? 's' : ''} même{open.length > 1 ? 's' : ''} dans toutes les images.</strong>{' '}
              Enregistrer depuis la page de contenu concernée réaligne les images. Si l'écart est voulu (par exemple un
              tchat plus court sur le Lobby), marque-le comme tel : il reviendra si l'un des fichiers change.
            </div>
          </div>
        )}
        {open.map((f) => (
          <div key={f.path} className={styles.file}>
            <div className={styles.fileHead}>
              <span className={styles.path}>{f.path}</span>
              {canAck && (
                <button className={styles.ackBtn} disabled={busy === f.path} onClick={() => void ack(f, true)}
                  title="L'écart ne s'affiche plus en alerte tant que ces fichiers ne changent pas">
                  C'est voulu
                </button>
              )}
            </div>
            <VariantLines file={f} />
          </div>
        ))}
        {acked.length > 0 && (
          <button className={styles.toggle} onClick={() => setShowAcked((v) => !v)}>
            {showAcked ? 'Masquer' : 'Voir'} {acked.length > 1 ? `les ${acked.length} écarts marqués` : "l'écart marqué"} comme voulu{acked.length > 1 ? 's' : ''}
          </button>
        )}
        {showAcked && acked.map((f) => (
          <div key={f.path} className={styles.file}>
            <div className={styles.fileHead}>
              <span className={styles.path}>{f.path}</span>
              <span className={styles.muted}>
                voulu{f.acknowledgedBy ? `, selon ${f.acknowledgedBy}` : ''}{f.acknowledgedAt ? `, le ${fmtDate(f.acknowledgedAt)}` : ''}
              </span>
              {canAck && (
                <button className={styles.ackBtn} disabled={busy === f.path} onClick={() => void ack(f, false)}>
                  Remettre en alerte
                </button>
              )}
            </div>
            <VariantLines file={f} />
          </div>
        ))}
        {report && report.notCompared.length > 0 && (
          <div className={styles.muted}>Non comparées : {report.notCompared.join(', ')}.</div>
        )}
      </div>
    </section>
  );
}

/**
 * Rappel compact pour le tableau de bord : seulement quand des fichiers de réglages divergent entre
 * les images du projet sans avoir été marqués voulus. Le détail est sur la page Publications.
 */
export function ImageDivergenceNotice() {
  const project = useGameStore((s) => s.activeGameId);
  const [report, setReport] = useState<DivergenceReportDTO | null>(null);

  useEffect(() => {
    let live = true;
    setReport(null);
    void apiService.getContainerDivergences().then((r) => { if (live && r.data) setReport(r.data); });
    return () => { live = false; };
  }, [project]);

  const open = report?.files.filter((f) => !f.acknowledged) ?? [];
  if (!report || open.length === 0) return null;
  return (
    <div className={`${styles.alert} ${styles.notice}`} role="status">
      <AlertTriangle size={14} aria-hidden />
      <div>
        <strong>Les images ne portent pas toutes les mêmes réglages</strong> : {open.length} fichier{open.length > 1 ? 's' : ''}{' '}
        ({open.slice(0, 3).map((f) => f.path).join(', ')}{open.length > 3 ? '…' : ''}).{' '}
        <Link to="/config/requests">Voir le détail dans Publications</Link>
      </div>
    </div>
  );
}
